<template>
    <div>
        <slot
            v-bind="{
                ...$data,
                decrementSubscriptionProducts,
                incrementSubscriptionProducts,
                replaceSubscriptionProducts,
                updateSubscription
            }"
        />
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import apiCall from '@/utils/apiCall';
import toasts from '@/mixins/toasts';

export default {
    mixins: [toasts],

    data: () => ({
        isSubscriptionUpdating: false,
        isSubscriptionUpdated: false,
        isSubmitting: false,
    }),

    methods: {
        ...mapActions(
            [
                'loadInvoices',
                'loadUpcomingInvoice',
                'loadUserSubscription',
                'getBillingSummary',
                'updateUser',
            ]
        ),

        /**
         * Decrements subscription products (off-boarding)
         *
         * @param  {number}   subscriptionId Subscription ID.
         * @param  {string[]} priceIds       List of price IDs.
         */
        async decrementSubscriptionProducts(subscriptionId, priceIds) {
            return await this.updateSubscriptionProducts(
                subscriptionId,
                'decrement',
                {
                    price_ids: priceIds,
                }
            );
        },

        /**
         * Increments subscription products (on-boarding)
         *
         * @param  {number}       subscriptionId Subscription ID.
         * @param  {string[]}     priceIds       List of price IDs.
         * @param  {string|null}  couponCode     Coupon code.
         */
        async incrementSubscriptionProducts(subscriptionId, priceIds, couponCode) {
            return await this.updateSubscriptionProducts(
                subscriptionId,
                'increment',
                {
                    price_ids: priceIds,
                    promo: couponCode,
                }
            );
        },

        /**
         * Increments subscription products (on-boarding)
         *
         * @param  {number}       subscriptionId Subscription ID.
         * @param  {string[]}     priceIds       List of price IDs.
         * @param  {string|null}  couponCode     Coupon code.
         * @param  {string[]}     priceToReplace Price replace.
         */
        async replaceSubscriptionProducts(subscriptionId, priceIds, couponCode, priceToReplace) {
            return await this.updateSubscriptionProducts(
                subscriptionId,
                'replace',
                {
                    price_ids: priceIds,
                    promo: couponCode,
                    price_to_replace: priceToReplace,
                }
            );
        },

        /**
         * Process user form
         *
         * @param {number} id     Subscription ID.
         * @param {string} method Method.
         * @param {object} params Request params.
         */
        async updateSubscriptionProducts(id, method, params) {
            this.isSubmitting = true;
            this.isSubscriptionUpdating = true;

            const { status, data } = await apiCall(
                this,
                'put',
                `/api/billing/subscriptions/${id}/products/${method}`,
                params
            );

            if (200 === status) {
                await this.refreshSubscription();
            }

            this.isSubmitting = false;
            this.isSubscriptionUpdating = false;

            return data;
        },

        /**
         * Update subscription payment interval.
         *
         * @param  {number} subscriptionId Subscription ID.
         * @param  {string} interval       New subscription payment interval.
         */
        async updateSubscription(
            subscriptionId,
            { interval = null, paymentMethod = null, couponCode = null } = {}
        ) {
            this.isSubmitting = true;
            this.isSubscriptionUpdating = true;

            const { data, status } = await apiCall(this, 'put', `/api/billing/subscriptions/${subscriptionId}`, {
                interval,
                payment_method: paymentMethod,
                promo: couponCode,
            });

            if (200 === status) {
                await this.refreshSubscription();
            }

            this.isSubmitting = false;
            this.isSubscriptionUpdating = false;

            return data;
        },

        /**
         * Refreshes user subscription data.
         */
        async refreshSubscription() {
            this.isSubscriptionUpdated = true;

            await this.updateUser();

            const subscription = this.loadUserSubscription({ force: true });
            const invoice = this.loadUpcomingInvoice();
            const invoices = this.loadInvoices({ force: true });
            const summary = this.getBillingSummary({ force: true });

            try {
                await Promise.all([
                    invoice,
                    invoices,
                    subscription,
                    summary,
                ]);

                this.$emit('subscription-updated');
            } catch (error) {
                this.toastError({
                    message: {
                        title: 'We couldn\'t update your Fusebox Pro Plan. Please try again later or contact us.',
                    },
                });
            }
        },
    },
};
</script>
